import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { TournamentPass } from '@fantaz/shared/data-access/models';
import { SharedDirectivesProcessingDirective } from '@fantaz/shared/directives/processing';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { CurrencyService } from '@fantaz/shared/utils';

@Component({
  selector: 'fz-pass-card',
  standalone: true,
  imports: [CommonModule, MaterialModule, SharedDirectivesProcessingDirective],
  templateUrl: './tournaments-ui-pass-card.component.html',
  styleUrls: ['./tournaments-ui-pass-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentsUiPassCardComponent {
  /**
   * Inject currency service to format the price
   */
  #currencyService: CurrencyService = inject(CurrencyService);

  /**
   * Tournament entry fee
   */
  @Input({ required: true })
  tournamentEntryFee: number;

  /**
   * Pass for tournament
   */
  @Input({ required: true })
  pass: TournamentPass;

  /**
   * Whether the card is pressed on primary action
   */
  @Output()
  press = new EventEmitter();

  /**
   * Whether the card is pressed on secondary action
   */
  @Output()
  share = new EventEmitter();

  get promoCode(): string {
    if (this.pass?.price?.promoCodes && this.pass?.price?.promoCodes?.length) {
      return this.pass?.price?.promoCodes[0].uuid ?? '';
    }
    return '';
  }

  get isUnlimited(): boolean {
    return this.pass?.entries >= 5000;
  }

  get canBeUsed() {
    if (this.pass?.price?.promoCodes && this.pass?.price?.promoCodes?.length) {
      return this.pass?.price?.promoCodes[0]?.canBeUsed == true;
    }
    return false;
  }

  get isRedeemed(): boolean {
    if (this.pass.purchaseLimitReached) {
      return true;
    }
    if (this.pass?.price?.promoCodes && this.pass?.price?.promoCodes?.length) {
      return !!this.pass.price.promoCodes[0]?.redeemDate && !this.canBeUsed;
    }
    return false;
  }

  get redeemedDate(): Date | undefined {
    if (this.pass?.price?.promoCodes && this.pass?.price?.promoCodes?.length) {
      return this.pass.price.promoCodes[0]?.redeemDate || undefined;
    }
    return undefined;
  }

  /**
   * Property to get the discount percent, if the pass is unlimited, return
   * empty string, if the price is 0, return 100% OFF, else calculate the
   * discount percent and return it with the percent symbol
   */
  get discountPercent(): number | string {
    if (this.isUnlimited) {
      return '';
    }

    const price = Number(this.pass?.price?.price);

    if (price == 0) {
      return '(100% OFF)';
    }

    const total = this.pass?.entries * this.tournamentEntryFee;
    const discount = Math.round((1 - price / total) * 100);

    return `(${discount}% OFF)`;
  }

  get havePass(): boolean {
    return (
      this.pass?.price?.promoCodes != null &&
      this.pass?.price?.promoCodes?.length > 0
    );
  }

  /**
   * Property to get the primary action text
   */
  get primaryActionText(): string {
    // convert the price to number
    const price = Number(this.pass.price.price);

    if (this.pass && this.pass?.purchased) {
      if (this.isUnlimited) {
        return 'Play - Unlimited';
      }

      if (this.pass.entries == 1) {
        return `Play - ${this.#currencyService.convertDollar(price)} USD`;
      }

      const remain = this.pass.entries - (this.pass.activeCount ?? 0) + 1;
      return `Play - ${remain}/${this.pass.entries}`;
    }

    if (this.pass.entries == 1) {
      const formattedPrice = this.#currencyService.convertDollar(price);
      return `Play - ${formattedPrice} USD`;
    }

    let pref = 'Buy';
    if (price == 0) {
      pref = `Play`;
    }

    return `${pref} - ${this.#currencyService.convertDollar(price)} USD`;
  }

  /**
   * Function to be called when the card is pressed on primary action
   */
  onPrimaryAction(event: MouseEvent) {
    this.press.emit(event);
  }

  /**
   * Function to be called when the promo code is pressed
   */
  onSharePromoCode(event: MouseEvent) {
    console.log('onSharePromoCode', event);
    this.share.emit(event);
  }
}
